import type { Item } from '@chaos-concierge/shared';

type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export type AppNotificationPermission = NotificationPermission | 'unsupported';

export function getNotificationPermission(): AppNotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<AppNotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.requestPermission();
}

function parseDateKey(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function dateKey(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

function recursOn(root: Item, targetKey: string): boolean {
  if (!root.recurrence || !root.date || targetKey <= root.date) return false;

  const start = parseDateKey(root.date);
  const target = parseDateKey(targetKey);
  if (!start || !target) return false;

  const end = root.recurrenceEnd ? parseDateKey(root.recurrenceEnd) : null;
  if (end && target > end) return false;

  const cursor = new Date(start);
  for (let i = 0; i < 730; i += 1) {
    switch (root.recurrence) {
      case 'daily':
        cursor.setDate(cursor.getDate() + 1);
        break;
      case 'weekly':
        cursor.setDate(cursor.getDate() + 7);
        break;
      case 'monthly':
        cursor.setMonth(cursor.getMonth() + 1);
        break;
      case 'yearly':
        cursor.setFullYear(cursor.getFullYear() + 1);
        break;
      default:
        return false;
    }

    const key = dateKey(cursor);
    if (key === targetKey) return true;
    if (key > targetKey) return false;
  }

  return false;
}

function rootFor(item: Item, byId: Map<string, Item>): Item {
  let current = item;
  while (current.parentId && byId.has(current.parentId)) {
    current = byId.get(current.parentId)!;
  }
  return current;
}

function isListItem(item: Item, byId: Map<string, Item>): boolean {
  if (item.listId) return true;
  let current = item.parentId ? byId.get(item.parentId) : undefined;
  while (current) {
    if (current.kind === 'list') return true;
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return false;
}

export function countTaskBadgeItems(items: Item[], targetKey: string): number {
  const byId = new Map(items.map((item) => [item.id, item]));
  const taskIds = new Set<string>();

  for (const item of items) {
    if (item.kind !== 'task' || item.done) continue;
    if (isListItem(item, byId)) continue;

    const root = rootFor(item, byId);
    const dueDate = item.date ?? root.date;
    if (item.needsAttention || dueDate === targetKey || (!item.date && recursOn(root, targetKey))) {
      taskIds.add(item.id);
    }
  }

  return taskIds.size;
}

export async function updateAppBadge(count: number): Promise<void> {
  if (typeof navigator === 'undefined') return;

  const badgeNavigator = navigator as BadgeNavigator;
  try {
    if (count > 0 && badgeNavigator.setAppBadge) {
      await badgeNavigator.setAppBadge(count);
    } else if (count === 0 && badgeNavigator.clearAppBadge) {
      await badgeNavigator.clearAppBadge();
    } else if (count === 0 && badgeNavigator.setAppBadge) {
      await badgeNavigator.setAppBadge(0);
    }
  } catch {
    // Badge support varies by browser/install state; badge failures should never disrupt planning.
  }
}

export function updateTaskAppBadge(items: Item[], targetKey: string): Promise<void> {
  return updateAppBadge(countTaskBadgeItems(items, targetKey));
}

export function clearTaskAppBadge(): Promise<void> {
  return updateAppBadge(0);
}
